import { pascalCase } from 'module/helpers/pascal-case';
import { unreachable } from 'module/helpers/unreachable';
import { MultiSelectField } from 'module/types/filter-field/field/filterable-field';
import { kebabCase } from 'lodash';
import {
  SelectFilterOperationType,
  valueFromString,
  valueToString,
} from './select';
import { TermSeparator } from '../separators';

interface MultiSelectFieldFilterBase {
  field: MultiSelectField;
}

interface MultiSelectFieldValueFilterBase extends MultiSelectFieldFilterBase {
  value?: number[];
}

interface MultiSelectIsFilter extends MultiSelectFieldValueFilterBase {
  type: SelectFilterOperationType.Is;
}

interface MultiSelectIsNotFilter extends MultiSelectFieldValueFilterBase {
  type: SelectFilterOperationType.IsNot;
}

interface MultiSelectIsOneOfFilter extends MultiSelectFieldValueFilterBase {
  type: SelectFilterOperationType.IsOneOf;
}

interface MultiSelectIsNoneOfFilter extends MultiSelectFieldValueFilterBase {
  type: SelectFilterOperationType.IsNoneOf;
}

interface MultiSelectIsEmptyFilter extends MultiSelectFieldFilterBase {
  type: SelectFilterOperationType.IsEmpty;
}

interface MultiSelectIsNotEmptyFilter extends MultiSelectFieldFilterBase {
  type: SelectFilterOperationType.IsNotEmpty;
}

export type MultiSelectFieldFilter =
  | MultiSelectIsFilter
  | MultiSelectIsNotFilter
  | MultiSelectIsOneOfFilter
  | MultiSelectIsNoneOfFilter
  | MultiSelectIsEmptyFilter
  | MultiSelectIsNotEmptyFilter;

const isValid = (filter: MultiSelectFieldFilter) => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return (filter.value ?? []).length > 0;
    default:
      return true;
  }
};

export const isOptional = (type: SelectFilterOperationType) =>
  type === SelectFilterOperationType.IsEmpty ||
  type === SelectFilterOperationType.IsNotEmpty;

const to = valueToString<number>((x) => x.toString());
const from = valueFromString((x) => {
  const result = parseInt(x, 10);

  return Number.isNaN(result) || Number.isNaN(Number(x))
    ? (x as unknown as number)
    : result;
});

const toString = (filter: MultiSelectFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return type;
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsNoneOf:
    case SelectFilterOperationType.IsOneOf:
      return [type, to(filter.value ?? [])].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (
  value: string,
  field: MultiSelectField,
): MultiSelectFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as SelectFilterOperationType;

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return { field, type, value: from(meta) };
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
    default:
      return unreachable(
        `${type} cannot be parsed for ${field.type} field` as never,
      );
  }
};

export const MultiSelectFieldFilter = {
  isValid,
  toString,
  parse,
};
